import { each, compact, flatten, map, includes } from 'lodash';
import { later } from '@ember/runloop';

import ActionController from './action-controller';
import HomeApi from 'mewe/api/home-api';
import PostApi from 'mewe/api/post-api';
import CommentsApi from 'mewe/api/comments-api';
import { insert, allPosts, removePost } from 'mewe/stores/models/feed-model';
import GroupStore from 'mewe/stores/group-store';
import Comment from 'mewe/stores/models/comment-model';
import { Post, updatePostFromEdit } from 'mewe/stores/models/post-model';
import { ds } from 'mewe/stores/ds';
import FunctionalUtils from 'mewe/shared/functional-utils';
import PostUtils from 'mewe/utils/post-utils';
import PostCommons from 'mewe/utils/posting-utils';
import PS from 'mewe/utils/pubsub';
import JSONSerializer from 'mewe/utils/store-utils/serializers/json-serializer';
import { run } from '@ember/runloop';
import {
  fetchFeed,
  fetchFeedMyWorld,
  fetchFeedProfile,
  fetchMeweOpenFeed,
  fetchFeedHashtag,
  getFeed,
  getProfileFeed,
  getGroupFeed,
  getGroupProfileFeed,
  getGroupsFeed,
  getEventFeed,
  getMyWorldFeed,
  getGroupPendingFeed,
  getEventPendingFeed,
  getAllFeed,
  getPagesFeed,
  getScheduledFeed,
  handleOtherStores,
  fetchPageFeedScheduled,
} from 'mewe/fetchers/fetch-feed';
import { getPage, fetchPage } from 'mewe/fetchers/fetch-pages';

import { FeedTypes, Theme, WrapperScope } from 'mewe/constants';
import { toPost } from 'mewe/stores/models/post-model';
import { inject as service } from '@ember/service';
import { getOwner } from '@ember/application';
import { A } from '@ember/array';
import EmberObject, { get, set } from '@ember/object';
import { assert } from '@ember/debug';
import Storage from 'mewe/shared/storage';
import Verbose from 'mewe/utils/verbose';
const verbose = Verbose({ prefix: '[Feed Action Controller]' }).log;

const Serializer = JSONSerializer.create();

export default ActionController.extend({
  routing: service('-routing'),
  dynamicDialogs: service(),
  account: service(),

  init() {
    this._super();

    PS.Sub('contact.remove', this.contactRemovedBlockedCleaning.bind(this));
    PS.Sub('user.blocked', this.contactRemovedBlockedCleaning.bind(this));
    PS.Sub('transcoding.job.done', this.removeVideoJobAndTempPost.bind(this));
  },

  contactRemovedBlockedCleaning(options) {
    // userId is required in this function
    if (!options.userId) {
      throw 'contactRemovedBlockedCleaning: missing userId';
    }

    const currentRouteName = this.routing.currentRouteName;

    // clear user's main profile feed, and refetch if on profile feed page as user can have some public posts visible for non contacts also
    getProfileFeed(options.userId).posts.clear();

    if (currentRouteName === 'app.publicid.posts') fetchFeedProfile(options.userId);

    // clear home feed as there can be blocked/removed contact's posts
    getMyWorldFeed().posts.clear();
    if (currentRouteName === 'app.myworld.index') fetchFeedMyWorld();

    // if group feed page then refetch feed, if not then clean that group's feed
    const groupRoute = getOwner(this).lookup(`route:app.group.index.feed`);

    if (groupRoute && currentRouteName === 'app.group.index.feed') {
      groupRoute.send('refetchFeed');
    } else if (options.groupId) {
      getGroupFeed(options.groupId).posts.clear();

      // clean user's group profile feed
      getGroupProfileFeed(options.userId, options.groupId).posts.clear();
    }

    // if all groups feed page then refetch feed, if not then clean all groups feed
    const groupsRoute = getOwner(this).lookup(`route:app.groups`);

    if (groupsRoute && currentRouteName === 'app.groups') {
      groupsRoute.send('refetchFeed');
    } else {
      getGroupsFeed().posts.clear();
    }

    // if event feed page then refetch feed, if not then clean that events's feed
    const eventRoute = getOwner(this).lookup(`route:app.event.feed`);

    if (eventRoute && currentRouteName === 'app.event.feed') {
      eventRoute.send('refetchFeed');
    } else if (options.eventId) {
      getEventFeed(options.eventId).posts.clear();
    }
  },

  openPostOrMediaDialog(post, params = {}) {
    // full page data is needed in SPV and MD (we have also simple page data attached to feeds that is not enough here)
    if (post.pageId && !post.get('page.isFetchedFullData')) {
      const page = getPage(post.pageId);
      if (!post.get('page.isFetchedFullData')) {
        fetchPage(post.pageId);
      }
      post.set('page', page);
    }

    // general approach is to open single photo post directly in MD unless other conditions don't block that
    let canOpenMediaDialog = post.mediasCount === 1 && !post.pending && !params.dontOpenMediaDialog;

    if (post.eventId || post.page) {
      // for events we don't open MD for single photo posts.
      // Firstly because for group events we can't fetch media for MD because of permissions.
      // Secondly, even if we allow MD for "open events", there is no event link in the MD anywhere so user opening link can't
      // go to event in any way, so that should be improved before we allow MD for events. For now simply disabled MD for event posts.
      canOpenMediaDialog = false;
    }

    if (canOpenMediaDialog) {
      this.dynamicDialogs.openDialog('media-dialog', {
        postId: post.id || post.postItemId,
        multiPostId: post.multiPostId || null,
        groupId: params.scope === Theme.GROUP ? params.groupId : null,
        eventId: params.scope === Theme.EVENT ? params.eventId : null,
        pageId: params.scope === Theme.PAGE ? params.pageId : null,
        isMyCloud: params.scope === Theme.MYCLOUD,
        hashtag: post.get('hashTags.length') === 1 && !post.album ? post.hashTags[0] : null,
        theme: params.scope,
        scope: params.scope,
        post: post,
        commentId: params.commentId,
        threadId: params.threadId,
        userId: params.userId,
        onClose: params.onClose,
        isSharedPost: params.isSharedPost,
        isRefPost: params.isRefPost,
        allowMultipleInstances: true,
        postContext: 'single_post_view',
        spvSource: params.spvSource,
      });
    } else {
      this.dynamicDialogs.openDialog('single-post-dialog', {
        post: post,
        theme: params.scope,
        commentId: params.commentId,
        onClose: params.onClose,
        isRefPost: params.isRefPost,
        allowMultipleInstances: true,
        spvSource: params.spvSource,
      });
    }
  },

  // options: postItemId, scope, groupId, eventId, threadId, commentId, doWithPost, fetchingPostForDialog
  // fetchingPostForDialog -> posts fetched in order to show it in dialog, should be stored separately from its default feedId
  // ^ this is to prevent updating this post if it already exists in feed, because fetched data can miss comments/replies already existing in feed post
  fetchPostFromMemoryOrServer(options) {
    if (!options) return;

    //when we have comment id fetch context and pass it
    if (options.commentId) {
      this.fetchCommentContext(options);
    } else {
      this.fetchPostFromMemoryOrServerFunc(options);
    }
  },

  fetchPostFromMemoryOrServerFunc(options) {
    if (!options) return;

    const { postItemId, groupId, pageId } = options;

    const posts = allPosts().filter((p) => {
      return (p.postItemId === postItemId || p.id === postItemId) && p.get('group.id') == groupId && p.pageId == pageId;
    });

    if (!options.fetchingPostForDialog && posts && posts.length && options.doWithPost) {
      // load post for single post dialog always from server SG-21602
      options.doWithPost(posts[0]);
    } else {
      this.fetchPostFromServer(options);
    }
  },

  // options: commentId, doWithPost, fetchingPostForDialog
  fetchCommentContext(options) {
    if (!options) return;

    CommentsApi.getCommentContext(options)
      .then((data) => {
        const post = data.post;

        handleOtherStores(data);

        post.threadId = options.threadId;

        // no info about number of loaded post's order so we assume that there can be more after and before
        post.comments = {
          contextCommentId: options.commentId,
          canBeMoreAfter: post.comments.total > 1,
          canBeMoreBefore: post.comments.total > 1,
          total: post.comments.total,
          feed: [],
        };

        //to speed up things they are deserialize as comments and then split
        const reply = data.comments.find((c) => c.replyTo !== undefined);
        const comment = data.comments.find((c) => c.replyTo === undefined);
        if (reply) comment.replies = Serializer.deserializeMany(A(), Comment, [reply]);
        post.comments.feed.push(comment);

        const { scope, scopeId } = options.fetchingPostForDialog
          ? { scope: 'single-posts', scopeId: 'single-posts' }
          : PostUtils.getPostScopeAndId(post);

        const feed = getFeed(scope, scopeId);

        const postE = toPost(post);

        feed.posts.unshiftObject(postE);

        options.doWithPost(postE);
      })
      .catch((resp) => {
        if (resp && resp.status === 404) {
          FunctionalUtils.error(__('Sorry, this comment has been deleted by the owner.'));
        }

        this.fetchPostFromMemoryOrServerFunc(options);
      });
  },

  // options: postItemId, scope, groupId, eventId, doWithPost, fetchingPostForDialog
  fetchPostFromServer(options) {
    if (!options) return;

    var modifyPost;

    if (options.scope === 'myworld') options.scope = Theme.CONTACTS;

    if (options.scope === Theme.CONTACTS) {
      modifyPost = (post) => {
        post.group = {
          id: Theme.CONTACTS,
          name: __('Home'),
        };
      };
    } else {
      // group or page
      modifyPost = () => {};
    }

    PostApi.getPostDetails({
      scope: options.scope,
      groupId: options.groupId,
      pageId: options.pageId,
      eventId: options.eventId,
      postItemId: options.postItemId,
      threadId: options.threadId,
      isPending: options.system === 'pending',
    })
      .then((data) => {
        if (!data || !data.post) {
          assert('feed-action-controller - post is missing in post details');
        }

        handleOtherStores(data);
        modifyPost(data.post);

        // in case we want to use data of fetched post (e.g. to update a field existing post)
        // but do not calculate the feed it belogs to and do not add post to that feed
        if (options.updatePostOnly && options.doWithPost) {
          options.doWithPost(data.post);
          return;
        }

        let scope = options.scope;

        // groupId can be undefined when main team group post
        let feedId = options.groupId || options.pageId || options.scope;

        // when fetching post for dialog, do not update it in feed because there could be more comments/replies
        // loaded already in feed-post than in fetched post, and it would disappear
        if (options.fetchingPostForDialog) {
          feedId = 'single-posts';
          scope = 'single-posts';
        }

        const feed = getFeed(feedId, scope);
        const postE = toPost(data.post);

        const existingPost = feed.posts.findBy('postItemId', postE.postItemId);

        // remove current post object before adding it again
        // (updating it by setProperties doens't work properly)
        if (existingPost) {
          feed.posts.removeObject(existingPost);
        }

        feed.posts.unshiftObject(postE);

        if (typeof options.doWithPost === 'function') {
          options.doWithPost(postE);
        }
      })
      .catch((resp) => {
        this.send('close');

        if (!resp) return;

        if (resp.status === 404) {
          FunctionalUtils.notice(
            __('This post may have been deleted or the person that shared it may have changed its permissions.')
          );
        } else if (resp.status === 403) {
          FunctionalUtils.error(__('Sorry, you do not have permission to view this post.'));
        } else if (resp.status !== 401) {
          FunctionalUtils.showDefaultErrorMessage();
        }
      });
  },

  removeWrapper(allWrappers, wrapper) {
    // it has to be postponed like this, somehow wrapper is not removed without that
    later(() => {
      allWrappers.removeObject(wrapper);
    });
  },

  isScrollPositionOk() {
    const feedEl = document.querySelector('.c-feed-simple');
    return !feedEl || feedEl.getBoundingClientRect().top > 0;
  },

  /**
   *  if there is no current hashtag set - no hashtag feed now - return true
   *  if there is hashtag feed - check if post contains current hashtag
   * */
  canAddIfHashtag(currentHashtag, postHashtags) {
    if (!currentHashtag) return true;

    if (postHashtags && postHashtags.length) {
      return postHashtags.includes(currentHashtag);
    }

    return false;
  },

  removeVideoJobAndTempPost({ jobId }) {
    // jobId is used as postItemId for tempPost
    removePost({ postItemId: jobId });
    this.send('removeVideoJob', jobId);
  },

  actions: {

    // tempPost that we store has jobId for identification and a bunch of post related data:
    // groupId, pageId, eventId, contacts, userId, createdAt, text.
    // It should be minimal version of post to display its placeholder in feed
    storeVideoJob(tempPost) {
      let videoPostJobs = Storage.getParsed(Storage.keys.pendingVideoPostJobs);
      if (!Array.isArray(videoPostJobs)) {
        videoPostJobs = [];
      }

      // add post to feed
      this.send('addPost', tempPost);

      // add post to local memory
      videoPostJobs.push(tempPost);
      Storage.set(Storage.keys.pendingVideoPostJobs, JSON.stringify(videoPostJobs));
    },

    removeVideoJob(jobId) {
      const videoPostJobs = Storage.getParsed(Storage.keys.pendingVideoPostJobs);
      if (!Array.isArray(videoPostJobs)) return;

      const existingJobIndex = videoPostJobs.findIndex((p) => p.jobId === jobId);
      if (existingJobIndex !== -1) {
        videoPostJobs.splice(existingJobIndex, 1);
      }

      if (!videoPostJobs.length) {
        // no more pending video jobs, remove completely from local memory
        Storage.remove(Storage.keys.pendingVideoPostJobs);
      } else {
        // update local memory
        Storage.set(Storage.keys.pendingVideoPostJobs, JSON.stringify(videoPostJobs));
      }
    },

    refreshFeedPostsAfterContactConnection(uuid) {
      allPosts().forEach((p) => {
        if (p.id == uuid) p.setProperties({ canComment: true, canEmojify: true });
      });
    },

    removePostFromWrapper(post) {
      const wrapperId = post.groupId || post.pageId || post.eventId;
      let allWrappers = ds.wrappers.items;

      if (!allWrappers || !allWrappers.length) return;

      // ! getting wrapper by getState({ id:..}) would create such empty wrapper even when not needed
      let wrapper = allWrappers.find((w) => w.id === wrapperId);

      if (!wrapper) return;

      let wrapperFeed = ds.feeds[`wrapper-${wrapperId}`];
      if (wrapperFeed && wrapperFeed.get('posts.length')) {
        let wrapperPosts = wrapperFeed.posts;
        let postToRemove = wrapperPosts.find((p) => p.id === post.postItemId);
        let wrapperCount = wrapper.postsCount;
        let newCount = wrapperCount > 1 ? wrapperCount - 1 : 0;

        // remove and decrease counter only if this post exists
        // don't remove post after WS if it's the last one in wrapper because of bad UX, remove only if I removed post as group owner
        if (postToRemove && (wrapperPosts.length > 1 || post.removedByMe)) {
          set(wrapper, 'postsCount', newCount);

          wrapperPosts.removeObject(postToRemove);

          if (newCount === 0) {
            PS.Pub('close.wrappers.dialog');
            this.removeWrapper(allWrappers, wrapper);
          }
        }
      }
    },

    clearWrappersPosts() {
      each(ds.wrappers.items, (w) => {
        const feed = getFeed(w.id, FeedTypes.WRAPPER);
        feed.posts.clear();
      });
    },

    refPostRemovedUpdate(postItemId) {
      // post was deleted or changed to private, check if it is shared in any post in feeds and set to unavailable
      const filterRefPosts = (p) => p.refPost && p.get('refPost.postItemId') === postItemId;

      let sharedPostsWithRef = compact(
        flatten(
          map(ds.feeds, (feed) => {
            if (!feed || !feed.posts || feed.id === 'mycloud') return;
            return feed.posts.filter(filterRefPosts);
          })
        )
      );

      each(sharedPostsWithRef, (p) => {
        p.set('refRemoved', true);
        p.set('refPost', null);
      });
    },

    refreshPostfeed(feed) {
      if (feed.isFetching || feed.isFetchingMore) return;

      let params = {};

      if (feed.tag) params.hashtag = feed.tag;
      if (feed.filter) params.filter = feed.filter;
      if (feed.maxResults) params.limit = feed.maxResults;
      if (feed.threadId) params.threadId = feed.threadId;
      if (feed.feedInHome) params.feedInHome = feed.feedInHome;

      feed.posts.clear();

      if (feed.apiCall) {
        fetchFeed(feed.apiCall, feed, params);
      } else {
        verbose('Missing feed.apiCall ', feed.type);
      }
    },

    loadMore(feed) {
      if (feed.isFetching || feed.isFetchingMore) return;

      let params = {};

      if (feed.paginationByOffset) {
        if (!feed.canLoadMore) return;
        params.offset = feed.offset;
      } else {
        if (!feed.nextPage) return;
        params.nextPage = feed.nextPage;
      }

      if (feed.tag) params.hashtag = feed.tag;
      if (feed.filter) params.filter = feed.filter;
      if (feed.maxResults) params.limit = feed.maxResults;
      if (feed.threadId) params.threadId = feed.threadId;

      switch (feed.type) {
        case FeedTypes.SCHEDULED_PAGE_POSTS:
          fetchPageFeedScheduled(feed.id.replace('scheduled-', ''), params);
          break;
        case FeedTypes.OPEN_FEED:
          params.locale = feed.locale;
          fetchMeweOpenFeed(params);
          break;
        case FeedTypes.HASHTAG:
          fetchFeedHashtag(params);
          break;
        default:
          if (feed.apiCall) {
            fetchFeed(feed.apiCall, feed, params);
          } else {
            verbose('Missing feed.apiCall ', feed.type);
          }
          break;
      }
    },

    addPost(post, dontAddToAllFeed) {
      if (!post) return;

      post = Serializer.deserializeOne(Post, post);

      const { scope, scopeId } = PostUtils.getPostScopeAndId(post);
      const feedScopeId = post.pending ? `pending-${scopeId}` : scopeId;
      const appTheme = getOwner(this).lookup(`route:app`).get('currentModel.theme');
      const currentRouteName = this.routing.currentRouteName;
      const currentHashtag = this.get('routing.currentState.routerJsState.queryParams.tag');
      const isMyPost = post.get('owner.id') === this.account.activeUser.id;
      const canAddIfHashtag = this.canAddIfHashtag(currentHashtag, post.hashTags);

      let isFeedTab, currentScopeId;

      switch (scope) {
        case Theme.PAGE:
          const appPage = getOwner(this).lookup('route:app.publicid');
          if (appPage) currentScopeId = appPage.get('currentModel.pageId');
          isFeedTab = currentRouteName === 'app.publicid.feed';

          if (post.pageId && !post.page.isFetchedFullData) {
            fetchPage(post.pageId);
          }

          if (isMyPost || (scopeId === currentScopeId && isFeedTab && this.isScrollPositionOk())) {
            const pageFeed = post.scheduled ? ds.feeds[`scheduled-${post.pageId}`] : ds.feeds[post.pageId];
            if (pageFeed) {
              if (canAddIfHashtag) {
                insert(pageFeed, post);
              }
            }

            // dismiss group alert if post is rendered in currently displayed feed
            if (!isMyPost) this.send('dismissWrappers', { pageIds: [scopeId] });
          } else if (!post.isScheduled) {
            this.dispatch('pages', 'increaseUnreadPostCounter', post);
            PS.Pub('wrapper.post.add', post);
          }

          // page post added also to 'all pages' and 'all feeds' feed
          if (!post.scheduled && !dontAddToAllFeed) {
            if (canAddIfHashtag) {
              insert(getPagesFeed(), post);
              insert(getAllFeed(), post);
            }
          }

          break;

        case Theme.EVENT:
          const appEvent = getOwner(this).lookup('route:app.event');

          if (appEvent) currentScopeId = appEvent.get('currentModel.eventId');
          isFeedTab = ~currentRouteName.indexOf('app.event.feed');

          if (post.pending) {
            insert(getEventPendingFeed(scopeId), post);
            const rootEvent = ds.eventPages[scopeId]?.rootEvent;
            if (rootEvent) rootEvent.set('hasPendingPosts', true);
          } else if (isMyPost || (isFeedTab && scopeId === currentScopeId && this.isScrollPositionOk())) {
            if (canAddIfHashtag) {
              getEventFeed(feedScopeId).posts.unshiftObject(post);
            }
          } else {
            this.dispatch('events', 'increaseUnreadPostCounter', post);
          }
          break;

        case Theme.CONTACTS:
          if (isMyPost || (appTheme === Theme.CONTACTS && this.isScrollPositionOk())) {
            if (canAddIfHashtag) {
              getMyWorldFeed(FeedTypes.ALL).posts.unshiftObject(post);
              getMyWorldFeed(feedScopeId).posts.unshiftObject(post);
            }
          }

          // Home feed post added also to owner's profile feed
          if (!post.scheduled) {
            getProfileFeed(post.get('owner.id')).posts.unshiftObject(post);
          } else {
            getScheduledFeed(post.get('owner.id')).posts.unshiftObject(post);
          }

          break;

        case Theme.GROUP:
          const appGroup = getOwner(this).lookup('route:app.group');

          if (appGroup) currentScopeId = appGroup.get('currentModel.groupId');
          isFeedTab = currentRouteName === 'app.group.index.feed';

          if (post.pending) {
            insert(getGroupPendingFeed(scopeId), post);

            GroupStore.getState({ id: scopeId }).set('hasPendingPosts', true);
          } else if (post.scheduled) {
            GroupStore.getState({ id: scopeId }).set('hasScheduledPosts', true);
          } else if (isMyPost || (scopeId === currentScopeId && isFeedTab && this.isScrollPositionOk())) {
            if (canAddIfHashtag) {
              insert(getGroupFeed(feedScopeId), post);
            }

            // dismiss group alert if post is rendered in currently displayed feed
            if (!isMyPost) this.send('dismissWrappers', { groupIds: [scopeId] });
          } else {
            this.dispatch('group', 'increaseUnreadPostCounter', post);
          }

          // group feed post added also to owner's group profile feed
          const groupUserFeedId = [post.get('owner.id'), scopeId].join('-');

          if (!post.scheduled) {
            getGroupProfileFeed(groupUserFeedId, feedScopeId).posts.unshiftObject(post);
          } else {
            insert(ds.feeds[`scheduled-${feedScopeId}`], post);

            // my scheduled group posts are also displayed in my profile scheduled feed
            if (isMyPost) {
              getScheduledFeed(post.get('owner.id')).posts.unshiftObject(post);
            }
          }

          const isGroupPostAndMuted = post.isGroupPost && GroupStore.getState({ id: scopeId }).groupMuted;

          if (this.isScrollPositionOk() && !post.pending && !isGroupPostAndMuted) {
            insert(getGroupsFeed(), post);
            insert(getAllFeed(), post);
          }

          break;
      }

      if (appTheme !== Theme.MYCLOUD) {
        if (post.files && post.files.length) PS.Pub('documents.reload');

        if (post.album) PS.Pub('photoAlbums.reload', post.album);

        if (post.get('medias.length')) {
          PS.Pub('photoStream.update', { post: post, type: 'add' });
          PS.Pub('photoTags.reload');
        }
      } else if (isMyPost) {
        // update for MC when posted in other tab, photostream only for now so in separate case for clarity
        if (post.get('medias.length')) {
          PS.Pub('photoStream.update', { post: post, type: 'add' });
        }
      }
    },

    openSinglePost(params) {
      this.fetchPostFromMemoryOrServer(
        Object.assign(params, {
          fetchingPostForDialog: true,
          doWithPost: (post) => {
            // home feed posts opened from link need userId to get user mediafeed in MD instead of timeline mediafeed
            if (params.isPostFromUrl && params.scope === Theme.CONTACTS) {
              params.userId = post.userId;
              params.scope = Theme.PROFILE;
            }
            this.openPostOrMediaDialog(post, params);
          },
        })
      );
    },

    fetchPostFromMemoryOrServer(options) {
      this.fetchPostFromMemoryOrServer(options);
    },

    deletePost(post, deletePostCallback) {
      this.dynamicDialogs.openDialog('remove-dialog', {
        post: post,
        deletePostCallback: deletePostCallback,
      });
    },

    deletePosts({ posts, message, scheduled }) {
      this.dynamicDialogs.openDialog('remove-dialog', {
        posts: posts,
        message: message,
        scheduled: scheduled,
      });
    },

    closePostPollOnNotification(postItemId) {
      let feeds = ds.feeds;

      run(() => {
        each(feeds, (f) => {
          let posts = f.posts.filterBy('postItemId', postItemId);
          each(posts, (p) => {
            p.set('poll.closed', true);
          });
        });
      });
    },

    rejectPost(postItemId, eventId, groupId, rejectCallback) {
      const scope = eventId ? Theme.EVENT : Theme.GROUP;
      const scopeId = eventId || groupId;
      const failure = () => FunctionalUtils.showDefaultErrorMessage();
      const callback = function () {
        if (typeof rejectCallback === 'function') rejectCallback();

        let feedPosts = getFeed(scopeId, eventId ? FeedTypes.EVENT_PENDING : FeedTypes.GROUP_PENDING).get('posts');
        const foundPost = feedPosts.find((p) => p.postItemId === postItemId);

        if (foundPost) feedPosts.removeObject(foundPost);
      };

      PostApi.rejectPost(postItemId, scope, scopeId).then(callback).catch(failure);
    },

    approvePost(postItemId, eventId, groupId, approveCallback) {
      const scope = eventId ? Theme.EVENT : Theme.GROUP;
      const scopeId = eventId || groupId;
      const failure = () => FunctionalUtils.showDefaultErrorMessage();
      const callback = function () {
        if (typeof approveCallback === 'function') approveCallback();

        let feedPosts = getFeed(`pending-${scopeId}`, eventId ? FeedTypes.EVENT_PENDING : FeedTypes.GROUP_PENDING).get(
          'posts'
        );
        const foundPost = feedPosts.find((p) => p.postItemId === postItemId);

        if (foundPost) feedPosts.removeObject(foundPost);

        FunctionalUtils.info(__('Post approved'));
      };

      PostApi.approvePost(postItemId, scope, scopeId).then(callback).catch(failure);
    },

    editPost: function (params, postboxHandler) {
      delete params.edit;

      delete params.poll; // not supported yet so no need to pass

      if (postboxHandler) postboxHandler.set('isSharing', true);
      else postboxHandler = EmberObject.create(params); // may be not defined in case of creating google/office document, but then groupId/eventId should be there and can be used for getting scope

      const scope = PostCommons.scope(postboxHandler);
      const post = postboxHandler.get('postToRepost');

      if (postboxHandler.get('preselectedFiles.length')) {
        params.existingFileIds = postboxHandler.get('preselectedFiles').map((f) => f.id);
      } else {
        params.existingFileIds = []; // delete all old files
      }

      if (get(params, 'fileIds.length')) {
        params.fileIds = params.fileIds.filter((id) => !includes(params.existingFileIds, id));
      }

      if (params.imageIds && params.imageIds.value) {
        params.imageIds = params.imageIds.value();
      }

      // imageIds: added photos, mediaIds: existing photos to keep
      if (!params.mediaIds) {
        params.mediaIds = []; // delete all old photos
      }

      if (postboxHandler.sticker) {
        params.stickers = [postboxHandler.sticker];
      } else {
        params.stickers = []; // delete old
      }

      if (!params.text) params.text = '';

      const state = PostUtils.getPostScopeAndId(post);

      if (state.scope === Theme.PRIVATEPOSTS) {
        params.threadId = post.get('threadId');
      }

      // TODO: allow removing video
      PostApi.editPost(params, post.get('id'), state)
        .then((data) => {
          if (!data || !data.post) return;

          run(() => {
            // or album was changed!?
            if (data.post.album && !post.get('album')) {
              PS.Pub('album.shared', params.album);
            }

            const mediaWasAdded = get(params, 'imageIds.length') || get(params, 'videoIds.length');

            if (mediaWasAdded) {
              const postWithMedias = data.post || { medias: data.medias }; // posting to MC don't return post object, just medias
              PS.Pub('photoStream.update', { post: postWithMedias, type: 'add' });
              PS.Pub('photoTags.reload');
            }

            const mediaWasRemoved = post.get('medias.length') > (get(data, 'post.medias.length') || 0);

            if (mediaWasRemoved) {
              const latestMedia = get(data, 'post.medias');

              let removedMedia;

              if (!latestMedia || !latestMedia.length) removedMedia = post.medias;
              else {
                removedMedia = post.medias.filter((m) => !latestMedia.find((l) => l.mediaId === m.mediaId));
              }

              PS.Pub('photoStream.update', { removeIds: removedMedia.map((m) => m.postItemId), type: 'remove' });
            }

            const postFiles = post.files || [];
            const filesChanged =
              get(params, 'fileIds.length') ||
              get(params, 'existingFileIds.length') !== postFiles.length ||
              postFiles.find((f) => !params.existingFileIds.find((existing) => existing.id == f.id));

            if (filesChanged) {
              PS.Pub('documents.reload');
              data.post.files = data.post.files.map((file) => {
                if (get(file, '_links.url.href')) {
                  const cacheBust = 'edit=' + encodeURIComponent(Math.random().toString(36).substring(2));
                  file._links.url.href =
                    file._links.url.href + (file._links.url.href.indexOf('?') == -1 ? '?' : '&') + cacheBust;
                }

                return file;
              });
            }

            if (!PostCommons.isPostingToCurrentLocation(state.scope, post, this.routing)) {
              FunctionalUtils.info(__('Post edited'));
            }

            postboxHandler.close(true);

            // thumbnail sizes are not in response and should be taken from previous link info fetching
            if (params.link && params.link.thumbnailUrl && postboxHandler.get('linkThumbUrlHeight')) {
              if (post.link)
                post.link.thumbnailSize = {
                  width: postboxHandler.get('linkThumbUrlWidth'),
                  height: postboxHandler.get('linkThumbUrlHeight'),
                };
            }

            if (!PostingUtils.isPostingToCurrentLocation(state.scope, post, this.routing)) {
              FunctionalUtils.info(__('Post edited'));
            }

            if (params.schedule) {
              FunctionalUtils.info(__('Your post has been scheduled'));
            }

            const dataPost = Serializer.deserializeOne(Post, data.post);

            const changePost = (E) => {
              if (E) updatePostFromEdit(post, dataPost);
            };

            changePost(post);

            const postId = post.get('postItemId') || post.get('id');
            const postsInStores = allPosts().filter((p) => p.postItemId === postId || p.id === postId);

            postsInStores.forEach(changePost);

            if (post.pending) {
              const currentRouteName = this.routing.get('currentRouteName');

              if (currentRouteName === 'app.event.feed' && post.eventId)
                dispatcher.dispatch('app', 'goTo', 'app.event.feed-pending', [post.eventId]);
              if (currentRouteName === 'app.group.index.feed' && post.groupId)
                dispatcher.dispatch('app', 'goTo', 'app.group.index.feed-pending', [post.groupId]);
            }
          });

          if (postboxHandler.get('presetEmojis')) {
            PostCommons.presetEmojis(postboxHandler.get('presetEmojis'), data.post, postboxHandler.get('target'));
          }

          if (params && params.callback) params.callback(post);
        })
        .finally(() => postboxHandler.alwaysAfterPosting())
        .catch((data) => {
          if (data && data.status === 403) {
            FunctionalUtils.error(__("Sorry, this post can't be edited"));
          } else if (data.data && data.data.message === 'New Poll cannot end in the past.') {
            FunctionalUtils.error(__('Check your system time settings. Poll cannot end in the past.'));
          } else if (data.data && data.data.errorCode === 700) {
            this.dynamicDialogs.openDialog('store/store-item-storage-dialog', { storageAlert: true });
          } else if (!data.data) FunctionalUtils.showErrorRetryPost(() => this.editPost(params, postboxHandler));
          else FunctionalUtils.showDefaultErrorMessage();
        });
    },

    dismissWrappers(params, dontClearFeed) {
      if (params.eventToDismiss) {
        this.dispatch(
          'events',
          'markEventFeedAsSeen',
          params.eventToDismiss.id,
          params.eventToDismiss.get('group.id') || null
        );
        getFeed(`wrapper-${params.eventToDismiss}`, FeedTypes.WRAPPER).posts.clear();
        this.removeWrapper(
          ds.wrappers.items,
          ds.wrappers.items.find((w) => w.id === params.eventToDismiss.id)
        );
      } else {
        HomeApi.dismissWrappers(params).then(() => {
          let wrappers = ds.wrappers.items;

          if (!wrappers) return;

          if (params.all || params.allGroups) {
            params.groupIds = wrappers.filter((i) => i.scope === WrapperScope.GROUP).map((i) => i.id);

            // this will reset all groups, not only those from loaded wrappers because there can be more paginated
            GroupStore.send('resetAllNewPostsCounters');
          }

          if (params.all || params.allPages) {
            params.pageIds = wrappers.filter((i) => i.scope === WrapperScope.PAGE).map((i) => i.id);

            // wrappers can be not loaded so check all loaded pages for newPosts counter
            const pagesWithNewPosts = ds.followed.items.filter((i) => i.newPosts > 0);
            each(pagesWithNewPosts, (p) => this.dispatch('pages', 'resetUnreadPostCounter', p.id));
          }

          each(params.groupIds, (groupId) => {
            GroupStore.send('resetNewPostsCounter', groupId);
            if (!dontClearFeed) getFeed(`wrapper-${groupId}`, FeedTypes.WRAPPER).posts.clear();
            this.removeWrapper(
              ds.wrappers.items,
              ds.wrappers.items.find((w) => w.id === groupId)
            );
          });

          each(params.pageIds, (pageId) => {
            this.dispatch('pages', 'resetUnreadPostCounter', pageId);
            if (!dontClearFeed) getFeed(`wrapper-${pageId}`, FeedTypes.WRAPPER).posts.clear();
            this.removeWrapper(
              ds.wrappers.items,
              ds.wrappers.items.find((i) => i.id === pageId)
            );
          });
        });
      }
    },

    fetchPostFromServer(options) {
      this.fetchPostFromServer(options);
    },

    updateGroupPostsAfterApplication(options = {}) {
      const groupPosts = allPosts().filter((p) => p.groupId === options.groupId);
      const newProperties = {
        placeholderApplication: true,
        'group.alreadyApplied': true,
        acceptedInvitation: options.acceptedInvitation,
      };

      if (groupPosts.length) {
        groupPosts.forEach((p) => {
          p.setProperties(newProperties);
        });
      }
    },

    updatePagePostsAfterFollowing(options = {}) {
      const pagePosts = allPosts().filter((p) => p.pageId === options.pageId);
      const newProperties = {
        placeholderApplication: true,
      };

      if (pagePosts.length) {
        pagePosts.forEach((p) => {
          p.setProperties(newProperties);
        });
      }
    },
  },
});
